import * as React from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import { CONTENT_TYPE } from "~constants";
import { LiveBlogPosts } from "~components/shared/liveblog";
import {
    type MediaConfig,
    Media,
    sourceHasValidMedia,
    MediaFormat,
    DEFAULT_MEDIA_PRIORITY,
    getWinningMediaFormat,
} from "~components/shared/media/";
import PhoenixRelatedItems from "~components/shared/phoenix-related-items/PhoenixRelatedItems";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import TickerPills from "~components/shared/ticker-pills/TickerPills";
import { VariantBase } from "~components/shared/variant-base/VariantBase";
import { useLiveBlog } from "~hooks/useLiveBlog";
import { isOpinionItem } from "~models/transforms/eyebrow";
import {
    type CuratedItem,
    type VariationComponent,
    getRelatedStories,
    getTerminalBlogId,
    ZoneColumnCombo,
    getIsOpinionPageTheme,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContent2Up.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContent2Up.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];
const MEDIA_CONFIG: MediaConfig = {
    desktopMaxWidth: 459,
    aspectRatio: 3 / 2,
    className: styles.media,
};
const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.verticalVideo,
    ...DEFAULT_MEDIA_PRIORITY,
];
const MAX_STORIES = 2;

export const LiveBlogStoryContent = ({
    story,
    storyIndex,
}: {
    story: CuratedItem;
    storyIndex: number;
}) => {
    const { url } = story;
    const terminalBlogId = getTerminalBlogId(story);
    const { liveBlog, loadingStatus: liveBlogLoadingStatus } =
        useLiveBlog(terminalBlogId);
    const isComingUp =
        liveBlog?.state === "live" && liveBlog?.posts?.length === 1;

    return (
        <div className={styles.storyContent}>
            <StoryBlock
                storyIndex={storyIndex}
                story={story}
                withDek
                isBylineEligible
                liveBlogState={isComingUp ? "comingUp" : liveBlog?.state}
            />
            <LiveBlogPosts
                storyIndex={storyIndex}
                loaded={liveBlogLoadingStatus.loaded}
                failed={liveBlogLoadingStatus.failed}
                liveBlog={liveBlog}
                className={styles.liveBlogPosts}
                url={url}
            />
        </div>
    );
};

const ImagelessStory = ({
    story,
    storyIndex,
    theme,
}: {
    story: CuratedItem;
    storyIndex: number;
    theme: VariationComponent["theme"];
}) => {
    const { terminalTickers, type, relatedTitle } = story;

    const relatedStories = getRelatedStories(story);
    const terminalBlogId = getTerminalBlogId(story);
    const isOpinionStory = isOpinionItem(story);
    const isOpinionTheme = getIsOpinionPageTheme(theme);

    const isLiveBlog =
        type === CONTENT_TYPE.liveblog && Boolean(terminalBlogId);

    return (
        <div
            className={createClasses(
                styles.story,
                styles.imagelessStory,
                isOpinionStory && styles.opinionbackground,
                isOpinionTheme && styles.isOpinionTheme
            )}
        >
            {isLiveBlog ? (
                <LiveBlogStoryContent story={story} storyIndex={storyIndex} />
            ) : (
                <div className={styles.storyContent}>
                    <StoryBlock
                        story={story}
                        withDek
                        storyIndex={storyIndex}
                        isBylineEligible
                        withOptionalEyebrow={!isOpinionTheme}
                    />
                    <TickerPills
                        storyIndex={storyIndex}
                        tickerIds={terminalTickers}
                    />
                    <PhoenixRelatedItems
                        storyIndex={storyIndex}
                        relatedItems={relatedStories || []}
                        relatedTitle={relatedTitle}
                    />
                </div>
            )}
        </div>
    );
};

const StoryWithVerticalVideoMedia = (props: {
    story: CuratedItem;
    storyIndex: number;
    isOpinionStory: boolean;
    isOpinionTheme: boolean;
}) => {
    const { story, storyIndex, isOpinionStory, isOpinionTheme } = props;
    const relatedStories = getRelatedStories(story);
    const { relatedTitle, terminalTickers, media } = story;
    const { verticalVideo } = media || {};

    const isVerticalVideoDecoration = Boolean(
        verticalVideo?.isVideoCurationOverride
    );

    const useSmallPlayer = Boolean(verticalVideo?.useSmallPlayer);

    return (
        <div
            className={createClasses(styles.story, {
                [styles.opinionbackground]: isOpinionStory,
                [styles.isOpinionTheme]: isOpinionTheme,
            })}
        >
            <div className={styles.storyContent}>
                <div
                    className={createClasses({
                        [styles.verticalVideoDecorationWithSmallPlayer]:
                            isVerticalVideoDecoration && useSmallPlayer,
                    })}
                >
                    <Media
                        source={story}
                        priority={[MediaFormat.verticalVideo]}
                        config={MEDIA_CONFIG}
                    />

                    {isVerticalVideoDecoration && (
                        <StoryBlock
                            storyIndex={storyIndex}
                            story={story}
                            withDek
                            isBylineEligible
                            showVerticalVideoHeadline
                            withOptionalEyebrow
                        />
                    )}
                </div>

                <TickerPills
                    storyIndex={storyIndex}
                    tickerIds={terminalTickers}
                />

                <PhoenixRelatedItems
                    storyIndex={storyIndex}
                    relatedItems={relatedStories || []}
                    relatedTitle={relatedTitle}
                />
            </div>
        </div>
    );
};

const StoryWithMedia = (props: {
    story: CuratedItem;
    renderingLocation: ZoneColumnCombo;
    storyIndex: number;
    theme: VariationComponent["theme"];
}) => {
    const { story, storyIndex, theme } = props;
    const terminalBlogId = getTerminalBlogId(story);
    const relatedStories = getRelatedStories(story);
    const { relatedTitle, terminalTickers, type, url } = story;

    const isOpinionStory = isOpinionItem(story);
    const isOpinionTheme = getIsOpinionPageTheme(theme);

    const isLiveBlog =
        type === CONTENT_TYPE.liveblog && Boolean(terminalBlogId);

    const { liveBlog, loadingStatus: liveBlogLoadingStatus } =
        useLiveBlog(terminalBlogId);

    if (isLiveBlog) {
        return (
            <div
                className={createClasses(
                    styles.story,
                    isOpinionStory && styles.opinionbackground,
                    isOpinionTheme && styles.isOpinionTheme
                )}
            >
                <Media
                    source={story}
                    priority={DEFAULT_MEDIA_PRIORITY}
                    config={MEDIA_CONFIG}
                />

                <div className={styles.storyContent}>
                    <StoryBlock
                        storyIndex={storyIndex}
                        story={story}
                        withDek
                        isBylineEligible
                        withOptionalEyebrow={!isOpinionTheme}
                    />
                    <LiveBlogPosts
                        storyIndex={storyIndex}
                        loaded={liveBlogLoadingStatus.loaded}
                        failed={liveBlogLoadingStatus.failed}
                        liveBlog={liveBlog}
                        className={styles.liveBlogPosts}
                        url={url}
                    />
                </div>
            </div>
        );
    }

    const isVerticalVideo =
        getWinningMediaFormat(story, MEDIA_PRIORITY) ===
        MediaFormat.verticalVideo;

    if (isVerticalVideo) {
        return (
            <StoryWithVerticalVideoMedia
                story={story}
                storyIndex={storyIndex}
                isOpinionStory={isOpinionStory}
                isOpinionTheme={isOpinionTheme}
            />
        );
    }

    return (
        <div
            className={createClasses(
                styles.story,
                isOpinionStory && styles.opinionbackground,
                isOpinionTheme && styles.isOpinionTheme
            )}
        >
            <Media
                source={story}
                priority={MEDIA_PRIORITY}
                config={MEDIA_CONFIG}
            />

            <div className={styles.storyContent}>
                <StoryBlock
                    storyIndex={storyIndex}
                    story={story}
                    withDek
                    isBylineEligible
                    withOptionalEyebrow={!isOpinionTheme}
                />
                <TickerPills
                    storyIndex={storyIndex}
                    tickerIds={terminalTickers}
                />
                <PhoenixRelatedItems
                    storyIndex={storyIndex}
                    relatedItems={relatedStories || []}
                    relatedTitle={relatedTitle}
                />
            </div>
        </div>
    );
};

export default function LineupContent2Up(props: VariationComponent) {
    const { items, renderingLocation, theme } = props;

    const integrity = integrityCheck(props);
    if (integrity.length) return integrityError(integrity);

    return (
        <VariantBase
            source={props}
            gridClassName={createClasses(
                styles.grid,
                getIsOpinionPageTheme(theme) && styles.isOpinionTheme
            )}
            className={styles.LineupContentTwoUp}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            {items?.slice(0, MAX_STORIES).map((item, index) => {
                if (
                    !sourceHasValidMedia(item, MEDIA_PRIORITY) ||
                    item.variation === "imageless"
                ) {
                    return (
                        <ImagelessStory
                            storyIndex={index + 1}
                            key={item.id}
                            story={item}
                            theme={theme}
                        />
                    );
                }
                return (
                    <StoryWithMedia
                        storyIndex={index + 1}
                        story={item}
                        renderingLocation={renderingLocation}
                        key={item.id}
                        theme={theme}
                    />
                );
            })}
        </VariantBase>
    );
}
